// Encryption permissions — the /P integer, in both directions.
//
// A PDF's permission flags live in one 32-bit signed integer in the
// encryption dictionary. Bits 1–2 are reserved and must be 0; every bit
// above the defined range must be 1. So "everything allowed" is -4, not
// -1, and each denial CLEARS a bit.
//
// The flags only bite when the file is encrypted: a permissions password
// (owner password) with an empty user password is the usual shape —
// anyone can open it, only the owner can lift the restrictions. Viewers
// enforce this by convention, not by cryptography; the honest framing in
// the UI is "asks viewers to prevent", not "makes impossible".
import type { DocPermissions } from "./protocol.ts";

/** Bit values from PDF 32000-1 table 22 (revision 3+). */
const BITS: Record<keyof DocPermissions, number> = {
  print: 4,
  edit: 8,
  copy: 16,
  annotate: 32,
  form: 256,
  accessibility: 512,
  assemble: 1024,
  printHq: 2048,
};

export const PERMISSION_KEYS = Object.keys(BITS) as (keyof DocPermissions)[];

export const ALL_ALLOWED: DocPermissions = {
  print: true,
  edit: true,
  copy: true,
  annotate: true,
  form: true,
  accessibility: true,
  assemble: true,
  printHq: true,
};

export const NONE_ALLOWED: DocPermissions = {
  print: false,
  edit: false,
  copy: false,
  annotate: false,
  form: false,
  accessibility: false,
  assemble: false,
  printHq: false,
};

/** Permission set → the /P integer mupdf's `permissions=` option wants. */
export function permissionsToP(p: DocPermissions): number {
  // -4 = every bit set except the two reserved low bits.
  let v = -4;
  for (const key of PERMISSION_KEYS) if (!p[key]) v &= ~BITS[key];
  return v;
}

/** The /P integer → permission set (used for round-trip tests). */
export function permissionsFromP(v: number): DocPermissions {
  const out = {} as DocPermissions;
  for (const key of PERMISSION_KEYS) out[key] = (v & BITS[key]) !== 0;
  return out;
}

export const PERMISSION_LABELS: Record<keyof DocPermissions, string> = {
  print: "Printing",
  printHq: "High-resolution printing",
  copy: "Copying text and images",
  edit: "Changing the document",
  annotate: "Commenting and markup",
  form: "Filling in form fields",
  accessibility: "Extraction for accessibility",
  assemble: "Assembling pages (insert, delete, rotate)",
};

/** UI order: the things people actually restrict, first. */
export const PERMISSION_ORDER: (keyof DocPermissions)[] = [
  "print",
  "printHq",
  "copy",
  "edit",
  "annotate",
  "form",
  "assemble",
  "accessibility",
];

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "D:20260720095740+08'00'" → "20 Jul 2026, 09:57". Anything that
 *  doesn't parse is shown verbatim rather than hidden. */
export function formatPdfDate(raw: string): string {
  const m = /^D?:?(\d{4})(\d{2})(\d{2})(\d{2})?(\d{2})?/.exec(raw.trim());
  if (!m) return raw;
  const [, y, mo, d, hh, mm] = m;
  const month = MONTHS[Number(mo) - 1];
  if (!month) return raw;
  const date = `${Number(d)} ${month} ${y}`;
  return hh ? `${date}, ${hh}:${mm ?? "00"}` : date;
}

/** One-line summary for the properties panel. */
export function describePermissions(p: DocPermissions): string {
  const denied = PERMISSION_ORDER.filter((k) => !p[k]);
  if (denied.length === 0) return "Everything allowed";
  if (denied.length === PERMISSION_ORDER.length) return "Everything restricted";
  return `Restricted: ${denied.map((k) => PERMISSION_LABELS[k].toLowerCase()).join(", ")}`;
}
